import { useCallback } from 'react'

import { flattenUsage } from 'components/FileUsage'
import FileUsage from 'components/FileUsage/FileUsageComponent'
import { FileUsageState, FlattUsageTypeEnum } from 'components/FileUsage/types'
import Card from 'components/uiKit/Card'
import CheckboxItem from 'components/uiKit/Checkbox/CheckboxItem'

import { CheckedBlocksMap } from '../FileUsagesCheckboxList/FileUsagesCheckboxList'
import * as s from './FileUsageProjectItem.module.scss'

interface IFileUsageProjectItemProps {
  projectUsage: FileUsageState
  checkedBlocks: CheckedBlocksMap
  onToggleProject: (projectId: string, checked: boolean) => void
  onToggleSection: (projectId: string, sectionId: string, checked: boolean) => void
  onToggleBlock: (fileUsageId: string) => void
}

type CheckboxState = {
  id: string
  checked?: boolean
  indeterminate?: boolean
  children?: CheckboxState[]
}

type FlattCheckboxState = {
  id: string
  checked?: boolean
  indeterminate?: boolean
}

const getCheckboxNodeState = (
  state: CheckboxState,
  checkedBlocks: CheckedBlocksMap,
): CheckboxState => {
  if (state.children) {
    const childStates = state.children.map((child) => getCheckboxNodeState(child, checkedBlocks))
    return {
      id: state.id,
      checked: childStates.every((child) => child.checked),
      indeterminate:
        childStates.some((child) => child.checked) && !childStates.every((child) => child.checked),
      children: childStates,
    }
  }

  return {
    id: state.id,
    checked: checkedBlocks[state.id],
    indeterminate: false,
  }
}

export const getProjectState = (
  projectUsage: FileUsageState,
  checkedBlocks: CheckedBlocksMap,
): FlattCheckboxState[] => {
  const checkboxState: CheckboxState = { id: projectUsage.projectId, children: [] }
  projectUsage.sections.forEach((section) =>
    checkboxState.children?.push({
      id: section.id,
      children: section.blocks.map((block) => ({ id: block.usageId })),
    }),
  )

  const calculatedCheckboxState = getCheckboxNodeState(checkboxState, checkedBlocks)
  return [
    {
      id: calculatedCheckboxState.id,
      checked: calculatedCheckboxState.checked,
      indeterminate: calculatedCheckboxState.indeterminate,
    },
    ...(calculatedCheckboxState.children
      ? calculatedCheckboxState.children.flatMap((child) => [
          {
            id: child.id,
            checked: child.checked,
            indeterminate: child.indeterminate,
          },
          ...(child.children
            ? child.children.map((c) => ({
                id: c.id,
                checked: c.checked,
                indeterminate: c.indeterminate,
              }))
            : []),
        ])
      : []),
  ]
}

const FileUsageProjectItem: React.FC<IFileUsageProjectItemProps> = ({
  projectUsage,
  checkedBlocks,
  onToggleProject,
  onToggleSection,
  onToggleBlock,
}) => {
  const state = getProjectState(projectUsage, checkedBlocks)
  const handleToggle = useCallback(
    (usageState: FlattCheckboxState, type: FlattUsageTypeEnum) => {
      switch (type) {
        case FlattUsageTypeEnum.project:
          onToggleProject(usageState.id, !usageState.checked)
          break
        case FlattUsageTypeEnum.section:
          onToggleSection(projectUsage.projectId, usageState.id, !usageState.checked)
          break
        case FlattUsageTypeEnum.block:
          onToggleBlock(usageState.id)
      }
    },
    [onToggleProject, onToggleSection, projectUsage.projectId, onToggleBlock],
  )
  const flattenedUsage = flattenUsage(projectUsage)

  return (
    <Card
      hovered={false}
      key={projectUsage.projectId}
      name={`FileUsagesCheckboxList-${projectUsage.projectId}`}
      padding={32}
      fill
    >
      <div className={s.checkboxList}>
        {flattenedUsage.map((usage, index) => (
          <CheckboxItem
            indeterminate={state[index].indeterminate}
            item={{
              value: !!state[index].checked,
              label: (
                <FileUsage gap={index === flattenedUsage.length - 1 ? false : true} usage={usage} />
              ),
            }}
            key={`${usage.type}-${usage.name}-${index}`}
            name={`FileUsagesCheckboxList-${state[index].id}`}
            onChange={() => handleToggle(state[index], usage.type)}
            value={state[index].checked}
          />
        ))}
      </div>
    </Card>
  )
}

export default FileUsageProjectItem
